export interface ComposerChatDonePayload {
  response_text: string;
  citations?: { label: string }[];
  has_insertable_content?: boolean;
  chat_mode: string;
  research_no_results?: boolean;
}

interface StreamHandlers {
  onDelta: (text: string) => void;
  onDone: (payload: ComposerChatDonePayload) => void;
  onError: (message: string) => void;
  onStatus?: (phase: string) => void;
}

/** Parse one SSE frame and dispatch to handlers. Returns true if consumed. */
function dispatchSseFrame(rawEvent: string, handlers: StreamHandlers): boolean {
  if (!rawEvent.trim()) return false;

  let eventType = 'message';
  let dataLine = '';

  for (const line of rawEvent.split('\n')) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    if (line.startsWith('data:')) dataLine = line.slice(5).trim();
  }

  if (!dataLine) return false;

  let data: Record<string, unknown>;
  try {
    data = JSON.parse(dataLine) as Record<string, unknown>;
  } catch {
    return false;
  }

  if (eventType === 'delta' && typeof data.text === 'string') {
    handlers.onDelta(data.text);
  } else if (eventType === 'status' && typeof data.phase === 'string') {
    handlers.onStatus?.(data.phase);
  } else if (eventType === 'done') {
    handlers.onDone(data as unknown as ComposerChatDonePayload);
  } else if (eventType === 'error') {
    handlers.onError(typeof data.message === 'string' ? data.message : 'Something went wrong.');
  } else {
    return false;
  }
  return true;
}

function drainSseBuffer(buffer: string, handlers: StreamHandlers): string {
  let remaining = buffer;
  let boundary = remaining.indexOf('\n\n');
  while (boundary !== -1) {
    const rawEvent = remaining.slice(0, boundary);
    remaining = remaining.slice(boundary + 2);
    dispatchSseFrame(rawEvent, handlers);
    boundary = remaining.indexOf('\n\n');
  }
  return remaining;
}

/** Parse SSE frames from composer-chat (event/data pairs separated by blank lines). */
export async function consumeComposerChatStream(
  response: Response,
  handlers: StreamHandlers,
): Promise<void> {
  if (!response.body) {
    handlers.onError('No response body from composer-chat');
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    buffer = drainSseBuffer(buffer, handlers);
  }

  // Flush any trailing frame not terminated by a blank line
  buffer += decoder.decode();
  if (buffer.trim()) {
    dispatchSseFrame(buffer, handlers);
  }
}

export function getLiveComposerDocumentContent(fallback: string): string {
  const getter = (window as Window & {
    __composerGetHtmlJson?: () => { html: string };
  }).__composerGetHtmlJson;

  return getter?.()?.html ?? fallback;
}
